import type { ANNAnnotationRecord } from "src/modules/ANNModule/ANNTypes";
import { SERVITUT_ANALYSIS_FIELDS, type ServitutAnalysisFieldKey } from "src/modules/ANNModule/ANNServitutFieldAnnotations";
import type { ServitutRow } from "./row";

export type AnnotationActor = {
   userId: string;
   userName: string;
};

/** Last person who saved the annotation; createdBy if it was never edited again. */
export function annotationSavedBy(record: Pick<
   ANNAnnotationRecord,
   "lastEditedBy" | "lastEditedByName" | "createdBy" | "createdByName"
>): AnnotationActor | undefined {
   const userId = String(record.lastEditedBy || record.createdBy || "").trim();
   if (!userId) {
      return undefined;
   }
   const userName = String(record.lastEditedByName || record.createdByName || userId).trim();
   return { userId, userName };
}

export function isAnnotationSavedByOtherUser(
   record: Pick<ANNAnnotationRecord, "lastEditedBy" | "lastEditedByName" | "createdBy" | "createdByName">,
   currentUserId?: string,
): boolean {
   const current = String(currentUserId || "").trim();
   if (!current) {
      return false;
   }
   const actor = annotationSavedBy(record);
   return Boolean(actor && actor.userId !== current);
}

export function firstForeignAnnotationSaver(
   records: Array<Pick<ANNAnnotationRecord, "lastEditedBy" | "lastEditedByName" | "createdBy" | "createdByName">>,
   currentUserId?: string,
): AnnotationActor | undefined {
   for (const record of records) {
      if (isAnnotationSavedByOtherUser(record, currentUserId)) {
         return annotationSavedBy(record);
      }
   }
   return undefined;
}

export function fieldUndoAnnotationIds(row: ServitutRow, field: ServitutAnalysisFieldKey): string[] {
   const id = row.fields[field].acceptedNoteId;
   return id ? [id] : [];
}

/** Document PDF plus the four analysis notes. Ignore notes are resolved separately. */
export function pageUndoAnnotationIds(row: ServitutRow): string[] {
   const ids: string[] = [];
   if (row.document.kind === "annotation" && row.document.recordId) {
      ids.push(row.document.recordId);
   }
   SERVITUT_ANALYSIS_FIELDS.forEach((field) => {
      const id = row.fields[field].acceptedNoteId;
      if (id) {
         ids.push(id);
      }
   });
   return ids;
}
